import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiServices from '../../../services/apiService';
import storageService from '../../../utils/storageService';
import PageHeadingSection from '../pageheading/PageHeadingSection';
import Breadcrumbs from '../BreadCrum/BreadCrum';
import ModuleAccordion from './ModuleAccordion';
import AddMasterPermissionModal from './AddMasterPermissionModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faSpinner, faPlus } from '@fortawesome/free-solid-svg-icons';

const RolePermissionsMobile = () => {
    const navigate = useNavigate();
    
    const [roles, setRoles] = useState([]);
    const [selectedRoleId, setSelectedRoleId] = useState("");
    
    const [modules, setModules] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isRoleLoading, setIsRoleLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Initial fetch of Roles and Master Modules
    const fetchInitialData = async () => {
            setIsInitialLoading(true);
            try {
                // Fetch Roles
                const rolesRes = await apiServices({
                    method: "GET",
                    url: `/RoleMaster/GetAllRoles`
                });
                
                let fetchedRoles = [];
                if (rolesRes.success) {
                    let apiData = rolesRes.data;
                    if (Array.isArray(apiData)) fetchedRoles = apiData;
                    else if (apiData && Array.isArray(apiData.data)) fetchedRoles = apiData.data;
                    else if (apiData && Array.isArray(apiData.RoleMasterData)) fetchedRoles = apiData.RoleMasterData;
                    setRoles(fetchedRoles);
                }

                // Fetch Modules
                const modulesRes = await apiServices({
                    method: "GET",
                    url: `/Permissions/modules`
                });
                
                if (modulesRes.success) {
                    let apiData = modulesRes.data;
                    let fetchedModules = [];
                    if (Array.isArray(apiData)) fetchedModules = apiData;
                    else if (apiData && Array.isArray(apiData.data)) fetchedModules = apiData.data;
                    
                    setModules(fetchedModules);
                } else {
                    toast.error("Failed to fetch modules");
                }
            } catch (error) {
                console.error("Initial fetch error:", error);
                toast.error("Error loading initial data.");
            } finally {
                setIsInitialLoading(false);
            }
        };

    useEffect(() => {
        fetchInitialData();
    }, []);

    // Fetch Role Permissions when a role is selected
    useEffect(() => {
        if (!selectedRoleId) {
            setSelectedIds([]);
            return;
        }

        const fetchRolePermissions = async () => {
            setIsRoleLoading(true);
            try {
                const roleRes = await apiServices({
                    method: "GET",
                    url: `/Permissions/${selectedRoleId}`
                });

                if (roleRes.success) {
                    const assignedKeys = roleRes.data?.permissions || roleRes.data?.data?.permissions || [];
                    
                    // Map the returned permissionKeys back to their IDs
                    let initialSelectedIds = [];
                    modules.forEach(module => {
                        if(module.features && Array.isArray(module.features)){
                            module.features.forEach(feature => {
                                if (assignedKeys.includes(feature.permissionKey)) {
                                    initialSelectedIds.push(feature.permissionId);
                                }
                            });
                        }
                    });

                    setSelectedIds(initialSelectedIds);
                } else {
                    toast.error("Failed to fetch role permissions");
                }
            } catch (error) {
                console.error("Error fetching permissions:", error);
                toast.error("An error occurred while fetching role permissions");
            } finally {
                setIsRoleLoading(false);
            }
        };

        fetchRolePermissions();
    }, [selectedRoleId, modules]);

    const handleTogglePermission = (permissionId) => {
        setSelectedIds(prev => {
            if (prev.includes(permissionId)) {
                return prev.filter(id => id !== permissionId);
            } else {
                return [...prev, permissionId];
            }
        });
    };

    const handleToggleModule = (module, selectAll) => {
        if(!module.features || !Array.isArray(module.features)) return;
        
        const modulePermissionIds = module.features.map(f => f.permissionId);
        
        setSelectedIds(prev => {
            let next = [...prev];
            if (selectAll) {
                // Add all ids from this module that aren't already selected
                modulePermissionIds.forEach(id => {
                    if (!next.includes(id)) next.push(id);
                });
            } else {
                // Remove all ids from this module
                next = next.filter(id => !modulePermissionIds.includes(id));
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!selectedRoleId) {
            toast.error("Please select a role first.");
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                roleId: selectedRoleId,
                permissionIds: selectedIds
            };

            const response = await apiServices({
                method: "POST",
                url: `/Permissions/update`,
                data: payload
            });

            if (response.success) {
                toast.success(response.message || "Permissions updated successfully");
            } else {
                toast.error(response.message || "Failed to update permissions");
            }
        } catch (error) {
            console.error("Error saving permissions:", error);
            toast.error(error.message || "An error occurred while saving permissions");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteMasterPermission = async (permissionId) => {
        if (!window.confirm("Are you sure you want to permanently delete this master permission? It will be removed from all roles.")) {
            return;
        }

        try {
            let employeeCode = storageService.getItem("employeeCode") || 1;
            
            const response = await apiServices({
                method: "DELETE",
                url: `/Permissions/master/${permissionId}?modifiedBy=${employeeCode}`
            });

            if (response.success && response.data?.isSuccess) {
                toast.success(response.data?.message || "Permission deleted successfully");
                // Refresh the modules list
                fetchInitialData();
            } else {
                toast.error(response.data?.message || response.message || "Failed to delete permission");
            }
        } catch (error) {
            console.error("Error deleting master permission:", error);
            toast.error("An error occurred while deleting the permission.");
        }
    };

    if (isInitialLoading) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 relative">
            <Breadcrumbs />
            <div className="flex justify-between items-start">
                <PageHeadingSection 
                    Navbar={true}
                    mainHeading={`Mobile Permissions`} 
                    subHeading="Select a role to manage its features"
                />
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="mt-4 px-4 py-2 bg-primaryaccent hover:bg-opacity-90 text-white text-xs font-bold rounded-lg flex items-center gap-2 shadow-sm transition-all"
                >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Master Permission
                </button>
            </div>
            
            {/* Role Selector */}
            <div className="mt-4 max-w-md">
                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                    Role <span className="text-red-500">*</span>
                </label>
                <select
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-bordercolor rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 dark:text-textprimary bg-white dark:bg-actionitembg transition-colors outline-none"
                >
                    <option value="">-- Select a Role --</option>
                    {roles.map(role => (
                        <option key={role.RoleId || role.roleId || role.Id || role.id} value={role.RoleId || role.roleId || role.Id || role.id}>
                            {role.RoleName || role.roleName}
                        </option>
                    ))}
                </select>
            </div>

            {/* Content */}
            <div className="flex-1 mt-6">
                {!selectedRoleId ? (
                    <div className="text-center py-10 text-gray-500">
                        Please select a role to view and manage its permissions.
                    </div>
                ) : isRoleLoading ? (
                    <div className="flex justify-center items-center py-10">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-gray-300" />
                    </div>
                ) : !Array.isArray(modules) || modules.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                        No modules found.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
                        {modules.map((module, idx) => (
                            <ModuleAccordion 
                                key={idx}
                                module={module}
                                selectedIds={selectedIds}
                                onTogglePermission={handleTogglePermission}
                                onToggleModule={handleToggleModule}
                                onDeletePermission={handleDeleteMasterPermission}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex justify-between mt-auto border-t border-bordercolor pt-4">
                <button 
                    onClick={() => navigate(-1)}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>
                <button 
                    onClick={handleSave}
                    disabled={isSaving || !selectedRoleId || isRoleLoading}
                    className="px-6 py-2 rounded-md text-white font-bold bg-primaryaccent hover:bg-opacity-90 disabled:opacity-50 text-xs flex items-center gap-2"
                >
                    {isSaving ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            Saving...
                        </>
                    ) : 'Save Changes'}
                </button>
            </div>

            {/* Add Master Permission Modal */}
            <AddMasterPermissionModal 
                isOpen={isAddModalOpen} 
                onClose={() => setIsAddModalOpen(false)} 
                onSuccess={fetchInitialData}
                modules={modules}
            />
        </div>
    );
};

export default RolePermissionsMobile;
